"use client";

import React, { createContext, useContext, useLayoutEffect, useState } from "react";

export type ThemeType =
  | "womens"
  | "mens"
  | "spring"
  | "summer"
  | "autumn"
  | "winter"
  | "sport-active"
  | "sport-club"
  | "poker-bar";

interface ThemeContextProps {
  theme: ThemeType;
  setTheme: (theme: ThemeType) => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

const DEFAULT_THEME: ThemeType = "womens";
const THEMES: ReadonlySet<string> = new Set<ThemeType>([
  "womens",
  "mens",
  "spring",
  "summer",
  "autumn",
  "winter",
  "sport-active",
  "sport-club",
  "poker-bar",
]);

export function resolveTheme(value: string | null): ThemeType {
  return value && THEMES.has(value) ? value as ThemeType : DEFAULT_THEME;
}

function readInitialTheme(): ThemeType {
  if (typeof window === "undefined") return DEFAULT_THEME;
  try {
    return resolveTheme(window.localStorage.getItem("axiom-manifold-theme"));
  } catch {
    return DEFAULT_THEME;
  }
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeType>(readInitialTheme);

  // Apply the resolved theme before the browser paints. The previous mount
  // effect hid the whole app until a second render, exposing cold-start work
  // as a blank-screen pause.
  useLayoutEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  const setTheme = (newTheme: ThemeType) => {
    setThemeState(newTheme);
    try {
      localStorage.setItem("axiom-manifold-theme", newTheme);
    } catch {
      // Theme selection still works when storage is unavailable.
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
